import styled, { css } from 'styled-components';

export const IconWrapper = styled.div`
  line-height: 0;
`;

export const LabelWrapper = styled.div`
  flex-grow: 1;
`;

export const buttonStyles = css`
  ${(props) =>
    props.fullWidth &&
    css`
      width: 100%;
    `}
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0;
  background: none;
  border: 1px solid transparent;
  border-radius: 6px;
  color: white;
  font: inherit;
  font-weight: normal;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  outline: none;
  transition: all 250ms ease;

  ${(props) =>
    (() => {
      switch (props.size) {
        case 'xsmall':
          return css`
            min-width: 28px;
            min-height: 28px;
            font-size: 14px;

            ${props.variant?.endsWith('-round') &&
            css`
              border-radius: 14px;
            `}

            ${props.hasLabel &&
            css`
              padding: 2px 8px;
            `}
          `;
        case 'small':
          return css`
            min-width: 32px;
            min-height: 32px;

            ${props.variant?.endsWith('-round') &&
            css`
              border-radius: 16px;
            `}

            ${props.hasLabel &&
            css`
              padding: 4px 8px;
            `}
          `;
        case 'regular':
        default:
          return css`
            min-width: 40px;
            min-height: 40px;

            ${props.variant?.endsWith('-round') &&
            css`
              border-radius: 20px;
            `}

            ${props.hasLabel &&
            css`
              padding: 8px 12px;
            `}
          `;
        case 'large':
          return css`
            min-width: 48px;
            min-height: 48px;
            gap: 12px;

            ${props.variant?.endsWith('-round') &&
            css`
              border-radius: 24px;
            `}

            ${props.hasLabel &&
            css`
              padding: 12px 20px;
            `}
          `;
      }
    })()}

  &:hover, &:focus {
    box-shadow: var(--shadow-base);
    text-decoration: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--outlineColor);
    text-decoration: none;
  }

  &:disabled {
    ${(props) =>
      props.disabledAlt
        ? css`
            background: none;
          `
        : css`
            background-color: var(--coolGray-300);
          `}
    box-shadow: none;
    color: var(--coolGray-400);
    cursor: not-allowed;

    &:hover,
    &:focus {
      ${(props) =>
        props.disabledAlt
          ? css`
              background: none;
            `
          : css`
              background-color: var(--coolGray-300);
            `}
      box-shadow: none;
      color: var(--coolGray-400);
    }
  }
`;

export const primaryButtonStyles = css`
  ${buttonStyles}
  background-color: var(--primary-900);
  font-weight: 600;

  &:hover {
    box-shadow: var(--shadow-lg);
  }

  &:hover,
  &:focus {
    background-color: var(--primary-1000);
  }
`;

export const secondaryButtonStyles = css`
  ${buttonStyles}
  background-color: var(--white);
  box-shadow: var(--shadow-base);
  color: var(--primary-900);

  &:hover {
    box-shadow: var(--shadow-lg);
  }

  &:hover,
  &:focus {
    background-color: var(--coolGray-100);
  }

  ${IconWrapper} {
    color: var(--primary-400);
  }
`;

export const tertiaryButtonStyles = css`
  ${buttonStyles}
  background-color: transparent;
  color: var(--primary-900);

  &:hover,
  &:focus {
    background-color: var(--indigo-50);
  }

  ${IconWrapper} {
    color: var(--blue-400);
  }
`;

export const destructiveButtonStyles = css`
  ${buttonStyles}
  background-color: var(--pink-900);

  &:hover,
  &:focus {
    background-color: var(--pink-1000);
  }
`;

const shouldForwardProp = (prop) => !['disabledAlt', 'fullWidth', 'hasLabel'].includes(prop);

export const Wrapper = styled('button').withConfig({ shouldForwardProp })`
  ${(props) =>
    (() => {
      switch (props.variant) {
        case 'primary':
        case 'primary-round':
          return primaryButtonStyles;
        case 'secondary':
        case 'secondary-round':
        default:
          return secondaryButtonStyles;
        case 'tertiary':
        case 'tertiary-round':
          return tertiaryButtonStyles;
        case 'destructive':
        case 'destructive-round':
          return destructiveButtonStyles;
      }
    })()}}
`;
